import type { PortalRole } from '@/api/types';

export type PortalKind = PortalRole;

export type PortalRoutingUser = {
  role?: string | null;
  hasActiveQuote?: boolean | null;
};

export const SELECT_PORTAL_PATH = '/select-portal';
export const PREFERRED_PORTAL_KEY = 'helio_preferred_portal';

export function isDualRole(user: PortalRoutingUser | null | undefined): boolean {
  if (!user) return false;
  return (user.role === 'installer' || user.role === 'admin') && Boolean(user.hasActiveQuote);
}

export function allowedPortals(user: PortalRoutingUser | null | undefined): PortalKind[] {
  const role = user?.role;
  if (role === 'installer') {
    return user?.hasActiveQuote ? ['installer', 'customer'] : ['installer'];
  }
  if (role === 'admin') {
    return user?.hasActiveQuote ? ['admin', 'installer', 'customer'] : ['admin', 'installer'];
  }
  return ['customer'];
}

export function hasMultiplePortals(user: PortalRoutingUser | null | undefined): boolean {
  return allowedPortals(user).length > 1;
}

export function canAccessPortal(
  user: PortalRoutingUser | null | undefined,
  portal: PortalKind,
): boolean {
  return allowedPortals(user).includes(portal);
}

export function portalPath(portal: PortalKind): string {
  switch (portal) {
    case 'installer':
      return '/installer';
    case 'admin':
      return '/admin';
    default:
      return '/portal';
  }
}

export function portalLabel(portal: PortalKind): string {
  switch (portal) {
    case 'installer':
      return 'Installer portal';
    case 'admin':
      return 'Admin portal';
    default:
      return 'Customer portal';
  }
}

export function defaultPortalPath(user: PortalRoutingUser | null | undefined): string {
  if (user?.role === 'installer') return '/installer';
  if (user?.role === 'admin') return '/admin';
  return '/portal';
}

export function portalFromPath(path: string): PortalKind | null {
  if (path === '/portal' || path.startsWith('/portal/')) return 'customer';
  if (path.startsWith('/installer/apply')) return null;
  if (path === '/installer' || path.startsWith('/installer/')) return 'installer';
  if (path === '/admin' || path.startsWith('/admin/')) return 'admin';
  return null;
}

/**
 * Where to send the user after login/register.
 * Returns null to stay on the current page (quote flow, already-allowed portal, chooser).
 */
export function postLoginPath(
  user: PortalRoutingUser,
  currentPath: string,
): string | null {
  if (
    currentPath === '/quote'
    || currentPath.startsWith('/quote?')
    || currentPath.startsWith('/quote/')
    || currentPath === '/plan'
    || currentPath.startsWith('/plan?')
    || currentPath.startsWith('/plan/')
  ) {
    return null;
  }
  if (currentPath === SELECT_PORTAL_PATH) {
    return null;
  }
  const currentPortal = portalFromPath(currentPath);
  if (currentPortal && canAccessPortal(user, currentPortal)) {
    return null;
  }
  if (isDualRole(user)) {
    return SELECT_PORTAL_PATH;
  }
  return defaultPortalPath(user);
}

export function getPreferredPortalPath(user: PortalRoutingUser | null | undefined): string {
  const preferred = readPreferredPortal();
  if (preferred && canAccessPortal(user, preferred)) {
    return portalPath(preferred);
  }
  return defaultPortalPath(user);
}

export function setPreferredPortal(portal: PortalKind): void {
  try {
    localStorage.setItem(PREFERRED_PORTAL_KEY, portal);
  } catch {
    // ignore quota / private mode
  }
}

export function clearPreferredPortal(): void {
  try {
    localStorage.removeItem(PREFERRED_PORTAL_KEY);
  } catch {
    // ignore
  }
}

function readPreferredPortal(): PortalKind | null {
  try {
    const value = localStorage.getItem(PREFERRED_PORTAL_KEY);
    if (value === 'customer' || value === 'installer' || value === 'admin') {
      return value;
    }
  } catch {
    // ignore
  }
  return null;
}
